(() => {
  const parseJson = (node, fallback) => {
    try { return JSON.parse(node?.textContent || ''); } catch (_) { return fallback; }
  };

  const initializeProduct = (section) => {
    if (!section || section.dataset.ezpInitialized === 'true') return;
    section.dataset.ezpInitialized = 'true';

    const variants = parseJson(section.querySelector('[data-ezp-variants]'), []);
    const labels = parseJson(section.querySelector('[data-ezp-labels]'), {});
    const form = section.querySelector('[data-product-form]');
    const variantInput = section.querySelector('[data-ezp-variant-id]');
    const price = section.querySelector('[data-ezp-price]');
    const comparePrice = section.querySelector('[data-ezp-compare-price]');
    const addButton = section.querySelector('[data-ezp-add]');
    const addLabel = section.querySelector('[data-ezp-add-label]');
    const addPrice = section.querySelector('[data-ezp-add-price]');
    const stock = section.querySelector('[data-ezp-stock]');
    const mobilePrice = section.querySelector('[data-ezp-mobile-price]');
    const mobileAdd = section.querySelector('[data-ezp-mobile-add]');
    const optionGroups = [...section.querySelectorAll('[data-option-index]')];


    const gallery = section.querySelector('[data-ezp-gallery]');
    const galleryStage = gallery?.querySelector('[data-ezp-gallery-stage]');
    const slides = [...(gallery?.querySelectorAll('[data-ezp-slide]') || [])];
    const thumbnails = [...(gallery?.querySelectorAll('[data-ezp-thumbnail]') || [])];
    let activeMediaIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

    const showMedia = (requestedIndex, focusThumbnail = false) => {
      if (!slides.length) return;
      const nextIndex = ((requestedIndex % slides.length) + slides.length) % slides.length;
      activeMediaIndex = nextIndex;

      const activeSlide = slides[nextIndex];
      const activeRatio = Number.parseFloat(activeSlide?.dataset.mediaRatio || '');
      if (galleryStage && Number.isFinite(activeRatio) && activeRatio > 0) {
        galleryStage.style.setProperty('--ezp-active-ratio', String(activeRatio));
      }

      slides.forEach((slide, index) => {
        const isActive = index === nextIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));

        slide.querySelectorAll('video').forEach((video) => {
          if (!isActive) video.pause();
          else if (video.autoplay) video.play().catch(() => {});
        });
      });

      thumbnails.forEach((thumbnail, index) => {
        const isActive = index === nextIndex;
        thumbnail.classList.toggle('is-active', isActive);
        thumbnail.setAttribute('aria-current', String(isActive));
      });

      const activeThumbnail = thumbnails[nextIndex];
      if (activeThumbnail) {
        const thumbnailRail = activeThumbnail.parentElement;
        if (thumbnailRail && thumbnailRail.scrollWidth > thumbnailRail.clientWidth) {
          const targetLeft = activeThumbnail.offsetLeft - ((thumbnailRail.clientWidth - activeThumbnail.offsetWidth) / 2);
          thumbnailRail.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
        }
        if (focusThumbnail) activeThumbnail.focus({ preventScroll: true });
      }
    };

    const showMediaById = (mediaId) => {
      const index = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      if (index >= 0) showMedia(index);
    };

    gallery?.querySelector('[data-ezp-gallery-prev]')?.addEventListener('click', () => showMedia(activeMediaIndex - 1));
    gallery?.querySelector('[data-ezp-gallery-next]')?.addEventListener('click', () => showMedia(activeMediaIndex + 1));
    thumbnails.forEach((thumbnail, index) => thumbnail.addEventListener('click', () => showMedia(index)));
    galleryStage?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showMedia(activeMediaIndex - 1, true);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        showMedia(activeMediaIndex + 1, true);
      }
    });

    const selectedOptions = () => optionGroups.map((group) => {
      const selected = group.querySelector('[data-option-value].is-active');
      return selected?.dataset.optionValue || '';
    });

    const findVariant = () => {
      const selected = selectedOptions();
      return variants.find((variant) => variant.options.every((value, index) => value === selected[index]));
    };

    const updateVariant = (scrollMedia = false) => {
      const variant = findVariant();
      const available = Boolean(variant?.available);

      if (variantInput) variantInput.value = variant?.id || '';
      if (price) price.textContent = variant?.price_formatted || '';
      if (mobilePrice) mobilePrice.textContent = variant?.price_formatted || '';

      if (comparePrice) {
        const showCompare = variant && variant.compare_at_price > variant.price;
        comparePrice.hidden = !showCompare;
        comparePrice.textContent = showCompare ? variant.compare_at_price_formatted : '';
      }

      if (addButton) addButton.disabled = !available;
      if (mobileAdd) mobileAdd.disabled = !available;
      if (addLabel) addLabel.textContent = !variant ? labels.unavailable : (available ? labels.add : labels.sold_out);
      if (addPrice) addPrice.textContent = available && labels.show_price ? ` — ${variant.price_formatted}` : '';
      if (mobileAdd) mobileAdd.textContent = !variant ? labels.unavailable : (available ? labels.mobile_add : labels.sold_out);

      if (stock) {
        stock.classList.toggle('is-sold-out', !available);
        const text = stock.querySelector('span:last-child');
        if (text) text.textContent = available ? labels.stock : labels.sold_out_stock;
      }

      if (variant && section.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      if (scrollMedia && variant?.featured_media_id) {
        showMediaById(variant.featured_media_id);
      }
    };

    optionGroups.forEach((group) => {
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;
          group.querySelectorAll('[data-option-value]').forEach((item) => {
            item.classList.toggle('is-active', item === button);
            item.setAttribute('aria-pressed', item === button ? 'true' : 'false');
          });
          const current = group.querySelector('[data-option-current]');
          if (current) current.textContent = button.dataset.optionValue;
          updateVariant(true);
        });
      });
    });

    const quantity = section.querySelector('[data-ezp-qty]');
    section.querySelector('[data-ezp-qty-minus]')?.addEventListener('click', () => {
      if (!quantity) return;
      quantity.value = Math.max(1, Number.parseInt(quantity.value || '1', 10) - 1);
    });
    section.querySelector('[data-ezp-qty-plus]')?.addEventListener('click', () => {
      if (!quantity) return;
      quantity.value = Math.max(1, Number.parseInt(quantity.value || '1', 10) + 1);
    });

    const accordionItems = [...section.querySelectorAll('.ezp-accordion')];
    const setAccordionState = (item, open) => {
      item.classList.toggle('is-open', open);
      const button = item.querySelector('.ezp-accordion-btn');
      if (!button) return;
      button.setAttribute('aria-expanded', String(open));
      const symbol = button.querySelector('span:last-child');
      if (symbol) symbol.textContent = open ? '−' : '+';
    };

    const initiallyOpen = accordionItems.filter((item) => item.classList.contains('is-open'));
    initiallyOpen.slice(1).forEach((item) => setAccordionState(item, false));

    accordionItems.forEach((item) => {
      const button = item.querySelector('.ezp-accordion-btn');
      button?.addEventListener('click', () => {
        const shouldOpen = !item.classList.contains('is-open');
        accordionItems.forEach((otherItem) => setAccordionState(otherItem, false));
        if (shouldOpen) setAccordionState(item, true);
      });
    });

    if (form) {
      form.addEventListener('submit', (event) => {
        if (!variantInput?.value) event.preventDefault();
      });
    }

    showMedia(activeMediaIndex);
    updateVariant(false);
  };

  const initializeVideos = () => {
    const videos = [...document.querySelectorAll('.ezp-main-product video, .ezp-fit video')];
    if (!videos.length || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        const gallerySlide = video.closest('[data-ezp-slide]');
        const canPlay = !gallerySlide || gallerySlide.classList.contains('is-active');
        if (entry.isIntersecting && canPlay && video.autoplay) video.play().catch(() => {});
        else video.pause();
      });
    }, { rootMargin: '140px 0px', threshold: 0.08 });
    videos.forEach((video) => observer.observe(video));
  };

  const initialize = (root = document) => {
    root.querySelectorAll?.('[data-ezp-product]').forEach(initializeProduct);
    initializeVideos();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
